/**
 * GuidedCaptureOverlay — the in-call guided capture the officer prompted
 * (S13-9 slice 1; SPEC-06 §3.3 PROMPTED class, applied at an S4-7 safe point).
 *
 * CAMERA HANDOFF (the design decision this component exists to get right):
 * the overlay NEVER calls getUserMedia. It attaches the call's ALREADY
 * PUBLISHED LiveKit local camera track to its own viewfinder <video> (LiveKit
 * tracks attach to any number of elements) and draws the still from that
 * element. Why:
 *   • A second getUserMedia on the same camera can STEAL the device on iOS
 *     Safari — the published call track goes black for the officer mid-call.
 *     Reusing the call's track means the LiveKit room and the recording are
 *     untouched: the media leg survives the capture by construction.
 *   • Capture integrity: the still provably comes from the SAME capture
 *     surface the officer is watching and the injection rail fingerprinted —
 *     an attacker cannot serve one stream to the call and another to the
 *     evidence frame, because there is only one track.
 *   • On top of that, the shared capture-integrity collector posts one more
 *     fingerprint attempt for this track WHILE the guided capture is open —
 *     temporally binding an injection scan to the prompted evidence (best-
 *     effort, never a gate on the customer).
 *
 * TRANSPARENCY (REG floor, no dark patterns): the overlay opens by SAYING who
 * asked (your verifier) and what for (the officer's reason CATEGORY rendered
 * in customer language — the verbatim reason never reaches this device), and
 * ack-on-show tells the server the customer has seen it. Declining is spoken:
 * the customer tells the verifier on the live call, the verifier withdraws the
 * prompt, and the overlay closes itself when the poll sees the withdrawal
 * (EXPIRED/CANCELLED are recorded outcomes server-side — nothing vanishes).
 *
 * Honesty rules inherited from SelfieCaptureCore: capture-only (no file
 * input, ever), exposure is the only pre-shot cue (no fabricated "looks
 * good"), the on-device quality gate is UX-only, retake is calm and never
 * red, object URLs are revoked, and no image data is ever logged.
 */

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
} from 'react';
import type { LocalVideoTrack } from 'livekit-client';
import { ApiError } from '@/api';
import { useT } from '@/i18n/runtime';
import {
  GCAP_DISCLOSURE_KEYS,
  GCAP_DISCLOSURE_VERSION,
  disclosureSha256,
} from '@/flow/disclosureVersion';
import {
  ackPrompt,
  fetchPendingPrompt,
  reportReady,
  uploadPromptCapture,
  uploadPromptDocument,
  type CapturePrompt,
  type ShutterActor,
} from '@/api/prompts';
import type { ArtifactKind } from '@/api/artifacts';
import { docTypeLabel, listDocumentRequirements } from '@/api/documents';
import { withStillQuality } from '@/media/stillCapture';
import type { FacingSwitchOutcome } from '@/media/switchFacing';
import { useCaptureIntegrity } from '@/media/useCaptureIntegrity';
import { useViewfinderExposure } from '@/media/useViewfinderExposure';
import { assessCaptureQuality, type QualityVerdict } from '@/media/imageQuality';
import { Button } from './Button';
import { GuideFrame } from './GuideFrame';
import { Icon } from './Icon';

/**
 * CAP-4 (KYCID·00001-00006-00002): the capture-QUALITY retry cap. Distinct from the post-divergence
 * recapture cap, which is an officer-side count with its own limit — two different counters for two
 * different acts, and conflating them was an error in an earlier draft of the spec.
 */
const MAX_CAPTURE_ATTEMPTS = 3;

const JPEG_QUALITY = 0.92;
const DONE_AUTORETURN_MS = 1_800;

/** Per-primitive rendering config — an unknown key never reaches this component (LiveCall filters). */
interface PromptConfig {
  readonly kind: ArtifactKind;
  readonly titleKey: string;
  readonly instructionKey: string;
  readonly faceGuide: CSSProperties | false;
  readonly cardGuide: CSSProperties | false;
}

const PROMPT_CONFIGS: Record<string, PromptConfig> = {
  'doc-show-hold': {
    kind: 'DOC_PHOTO',
    titleKey: 'droom.gcap.doc.title',
    instructionKey: 'droom.gcap.doc.instruction',
    faceGuide: false,
    cardGuide: { left: '14%', right: '14%', top: '30%', height: '40%', width: 'auto' },
  },
  'selfie-while-holding': {
    kind: 'SELFIE_WITH_DOC_FRAME',
    titleKey: 'droom.gcap.selfiedoc.title',
    instructionKey: 'droom.gcap.selfiedoc.instruction',
    faceGuide: { left: '36%', top: '40%', width: 124, height: 156 },
    cardGuide: { right: '7%', bottom: '20%', width: 110, height: 70 },
  },
  // PRIM-03 (KYCID·00001-00006-00002): guided plain live selfie → SELFIE_FRAME, the FACE_MATCH probe.
  // Added to the BE catalog + cockpit palette in that slice but the renderer here (and the DB constraint,
  // fixed in be V73) were missed — so a dispatched selfie-live prompt was silently un-renderable. A plain
  // selfie: a centred face oval, no document box. transform:'none' is load-bearing: .vp-guide-face
  // positions by CENTRE-POINT (translate(-50%,-50%)), but this guide stretches by EDGES — without the
  // override the class translate shifts the oval a quarter-frame up-left, clipped by the viewport.
  'selfie-live': {
    kind: 'SELFIE_FRAME',
    titleKey: 'droom.gcap.selfie.title',
    instructionKey: 'droom.gcap.selfie.instruction',
    faceGuide: { left: '25%', right: '25%', top: '14%', height: '64%', width: 'auto', transform: 'none' },
    cardGuide: false,
  },
};

/** True when this droom build knows how to render the prompted key (deploy-skew guard). */
export function isRenderablePrompt(primitiveKey: string): boolean {
  return primitiveKey in PROMPT_CONFIGS;
}

/** The normalised SPEC-13 D4 display block riding a prompt. */
export interface PromptDisplay {
  /** Registry docType key, when the prompt names one. */
  readonly docType: string | null;
  /** The registry display label, when served (wins over any client vocabulary). */
  readonly docTypeLabel: string | null;
  /** Per-type capture guidance line, when served. */
  readonly captureGuidance: string | null;
  /** Guide-frame aspect for the orientation advisory; null = no claim. */
  readonly guideAspect: 'LANDSCAPE' | 'PORTRAIT' | null;
  /**
   * Testmap #16 (ADDITIVE): which SIDE of the document this prompt asks for —
   * the officer's side-sequenced guided capture ("Now the back of the
   * document"). Absent = no side claim (single-side docs, older be) — the
   * generic prompt, never a guessed side.
   */
  readonly docSide: 'FRONT' | 'BACK' | null;
}

/**
 * SPEC-13 D4: normalise the prompt's ADDITIVE display fields. Tolerates both
 * camelCase and snake_case wire spellings (the be registry columns are snake;
 * its DTOs are camel — a droom must not break on either during the rollout).
 * Absent fields normalise to null — today's generic prompt, never a guess.
 */
export function promptDisplay(prompt: CapturePrompt): PromptDisplay {
  const raw = prompt as unknown as Record<string, unknown>;
  const str = (v: unknown): string | null =>
    typeof v === 'string' && v.trim() !== '' ? v.trim() : null;
  // Wire-pinned 2026-08-15: the be serves guideAspect as a NUMERIC width/height ratio
  // (>1 landscape, <1 portrait) and the label as docDisplayLabel — both accepted first,
  // with the older string/spelling fallbacks kept for skew tolerance.
  const aspectNum =
    typeof raw.guideAspect === 'number' && Number.isFinite(raw.guideAspect)
      ? raw.guideAspect
      : null;
  const aspectRaw = (
    str(raw.guideAspect) ?? str(raw.guide_aspect) ?? ''
  ).toUpperCase();
  const sideRaw = (str(raw.docSide) ?? str(raw.doc_side) ?? '').toUpperCase();
  return {
    docType: str(raw.docType) ?? str(raw.doc_type),
    docTypeLabel: str(raw.docDisplayLabel) ?? str(raw.doc_display_label)
      ?? str(raw.docTypeLabel) ?? str(raw.doc_type_label)
      ?? str(raw.displayLabel) ?? str(raw.display_label),
    captureGuidance: str(raw.captureGuidance) ?? str(raw.capture_guidance),
    guideAspect:
      aspectNum !== null
        ? (aspectNum > 1 ? 'LANDSCAPE' : aspectNum < 1 ? 'PORTRAIT' : null)
        : aspectRaw === 'LANDSCAPE' || aspectRaw === 'PORTRAIT' ? aspectRaw : null,
    // #16: closed side vocabulary — an unknown side makes NO claim (never a guessed side).
    docSide: sideRaw === 'FRONT' || sideRaw === 'BACK' ? sideRaw : null,
  };
}

/**
 * Is the screen currently portrait? (SPEC-13 D4 / item 19 — the orientation
 * ADVISORY.) matchMedia-driven with a live listener; environments without
 * matchMedia (older webviews, jsdom) honestly report false — the advisory
 * simply never shows, and it never blocks anything either way.
 */
function useIsPortrait(): boolean {
  const [portrait, setPortrait] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }
    const mq = window.matchMedia('(orientation: portrait)');
    const onChange = (): void => setPortrait(mq.matches);
    onChange();
    // Older Safari ships addListener only; feature-detect rather than crash.
    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', onChange);
      return () => mq.removeEventListener('change', onChange);
    }
    mq.addListener(onChange);
    return () => mq.removeListener(onChange);
  }, []);
  return portrait;
}

/**
 * The officer's category, in customer language — calm, factual, never a score
 * and never an accusation (the verbatim reason is officer-side material).
 * (The geo-recheck overlay deliberately does NOT reuse these lines — they are
 * photo-phrased; it says "asked to re-check your location" in its own words.)
 */
function categoryKey(category: string): string {
  switch (category) {
    case 'DOCUMENT_UNCLEAR':
      return 'droom.gcap.cat.unclear';
    case 'DOCUMENT_MISMATCH':
      return 'droom.gcap.cat.mismatch';
    case 'TAMPER_SUSPECTED':
      return 'droom.gcap.cat.tamper';
    case 'IDENTITY_DOUBT':
      return 'droom.gcap.cat.identity';
    case 'REGULATORY_REQUIREMENT':
      return 'droom.gcap.cat.regulatory';
    // DEC-071: the capture is a step of the configured flow — not a suspicion, not a regime claim.
    case 'FLOW_STEP':
      return 'droom.gcap.cat.flow';
    default:
      return 'droom.gcap.cat.default';
  }
}

interface Shot {
  blob: Blob;
  url: string;
  verdict: QualityVerdict;
}

export function GuidedCaptureOverlay({
  sessionId,
  prompt,
  localVideoTrack,
  camEnabled,
  onToggleCam,
  onSwitchCamera,
  onBusyChange,
  onDone,
  remoteShutterAt,
}: {
  sessionId: string;
  prompt: CapturePrompt;
  localVideoTrack: LocalVideoTrack | null;
  camEnabled: boolean;
  onToggleCam: () => void;
  /**
   * CAM-2/CAM-3: switch the PUBLISHED camera. The overlay never calls getUserMedia itself (a second
   * acquisition can steal the device on iOS Safari), so it asks the owner of the room.
   *
   * Resolves a THREE-valued outcome, and this component must respect all three: 'switched' is the
   * only one that licenses saying the lens changed; 'unverified' means the restart worked but the
   * browser will not describe the result, so we proceed and claim nothing; 'unavailable' is the
   * one-camera device, which must still complete the capture (CAM-5).
   */
  onSwitchCamera?: (facing: 'user' | 'environment') => Promise<FacingSwitchOutcome>;
  /** Safe-point signal to the poll: true while a shot/upload ceremony is in flight. */
  onBusyChange: (busy: boolean) => void;
  onDone: (outcome: 'completed' | 'withdrawn') => void;
  /**
   * V116 / owner item 4 — THE OFFICER-TRIGGERED SHUTTER. A monotonic tick that changes when the
   * verifier fires the shutter for THIS prompt (relayed by the server over the room's data channel,
   * or noticed on the ordinary poll when the relay did not arrive). 0/undefined = never fired.
   *
   * <p>A tick rather than a boolean so a second press is unambiguously a second event, and so this
   * component owns the "have I already acted on it" decision rather than the caller.
   *
   * <p>The officer commands the MOMENT, never the CONSENT: every local precondition below is
   * re-checked here before the camera is touched, and the purpose acknowledgement is a precondition
   * the officer's press can neither satisfy nor bypass.
   */
  remoteShutterAt?: number;
}): JSX.Element {
  const { t, locale } = useT();
  const config = PROMPT_CONFIGS[prompt.primitiveKey]!;

  // ── SPEC-13 D4: the prompt may NAME the document ───────────────────────────
  // Registry label + per-type guidance + guide aspect, all additive; an older
  // be serves none and this stays today's generic prompt. The served label
  // wins; a bare docType resolves through the deck vocabulary (localised),
  // falling back to the humanised EN label for deploy-skew names.
  const display = promptDisplay(prompt);
  const namedDoc = ((): string | null => {
    if (display.docTypeLabel) return display.docTypeLabel;
    if (!display.docType) return null;
    const key = `droom.doctype.${display.docType}`;
    const translated = t(key);
    return translated === key ? docTypeLabel(display.docType) : translated;
  })();
  // #16: a side-sequenced document prompt NAMES the side. The BACK title says "now" honestly —
  // the officer's sequence only reaches the back after the front landed; FRONT keeps the typed
  // title (saying "front" before a back exists would be noise on single-side documents).
  const title =
    prompt.primitiveKey === 'doc-show-hold' && display.docSide === 'BACK'
      ? namedDoc
        ? t('droom.gcap.doc.titleBack', { doc: namedDoc })
        : t('droom.gcap.side.backTitle')
      : prompt.primitiveKey === 'doc-show-hold' && namedDoc
        ? t('droom.gcap.doc.titleTyped', { doc: namedDoc })
        : t(config.titleKey);

  const [shot, setShot] = useState<Shot | null>(null);
  const [assessing, setAssessing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [done, setDone] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  // ── CAP-1/CAP-2/CAP-4: the capture CEREMONY ────────────────────────────────
  // CAP-2 is the load-bearing one and the easiest to get subtly wrong. DPDP s.6 requires consent
  // signified by a CLEAR AFFIRMATIVE ACTION, and a shutter press is an action about a PHOTOGRAPH,
  // not about the PURPOSE. So the purpose notice is acknowledged FIRST, as its own act, and only
  // then does the capture control become available. Merging the two — "press the shutter, that
  // counts as consent" — is exactly the collapse the rule forbids.
  const [purposeAcknowledged, setPurposeAcknowledged] = useState(false);
  // CAP-4: a hard retry cap. Unbounded retake is its own audit finding — "the customer retried until
  // the quality gate relented" reads the same way as an operator re-running until a model agrees.
  const [attempts, setAttempts] = useState(0);
  // CAP-4: WHY each discarded shot was discarded, oldest first. Counting attempts on the device was
  // never enough — a count with no reasons cannot answer the only question an examiner actually asks
  // ("how many times did they try, and what was wrong the first two times?"). Reasons are held here
  // and ride the accepted upload; the discarded BYTES are never sent.
  const discardedReasons = useRef<string[]>([]);
  const attemptsExhausted = attempts >= MAX_CAPTURE_ATTEMPTS;

  // ── CAM-1..CAM-5: the officer's camera preference ──────────────────────────
  // OWNER ROUND 2026-08-29 (item I2) — the preference is APPLIED, not offered. The first ceremony
  // rendered a card with a "Switch camera and continue" button and moved the lens only if the
  // customer tapped it. On a live call nothing draws attention to that optional tap — the customer
  // is reading the purpose notice, whose Agree button is the primary action, with an officer
  // talking to them — so the officer's "rear camera, for documents" routinely never ran and every
  // capture opened on the call's face-forward default. The owner's report is that experience
  // verbatim: "rear camera is selected but when camera opens at customer it is using default front
  // camera only". The 2026-08-26 fix repaired the getUserMedia constraint ladder UNDER the button;
  // this removes the button, which was the remaining reason the ladder never ran.
  //
  // What replaces the gate keeps every property CAM-3 was protecting, minus the tap nobody owes us:
  //   • DISCLOSED — a status line names the lens the verifier asked for while the switch runs, and
  //     the lines below say what actually happened (applied / one-camera / couldn't-switch /
  //     unconfirmed). Nothing is silent; it is simply not pre-gated.
  //   • REVERSIBLE — the customer's own flip toggle (SPEC-13 D4, item 18) stays live throughout:
  //     the lens remains theirs to take back at any moment.
  //   • BOUNDED — CAM-4 below still restores the face-forward lens the moment the overlay closes,
  //     so the call and the recording return to the customer's face without another ceremony.
  //   • The DPDP purpose acknowledgement is untouched: the SHUTTER stays behind the customer's own
  //     affirmative act. Consent guards the photograph — it never guarded which lens frames it.
  const requestedFacing = prompt.requestedFacing ?? null;
  const [facingApplied, setFacingApplied] = useState(false);
  const [facingUnavailable, setFacingUnavailable] = useState(false);
  /**
   * The 'unavailable' came from a device that PROVABLY has one camera (the ladder's own
   * enumeration says so). "We couldn't switch" would overstate that — nothing failed, there is
   * simply no second lens — so the copy says what is true instead: one camera, we are using it.
   */
  const [facingSingleCamera, setFacingSingleCamera] = useState(false);
  /**
   * The switch went through but the browser would not say WHICH lens is live. Distinct from
   * `facingUnavailable`, which is the browser telling us it did not switch. Saying nothing here
   * would be the dishonest option — the customer would be left to assume a rear camera we cannot
   * confirm — and saying "we couldn't switch" would be equally untrue. So we say exactly what we
   * know and point them at the viewfinder, which is on screen and can be checked in a second.
   */
  const [facingUnconfirmed, setFacingUnconfirmed] = useState(false);
  /**
   * The lens the OFFICER's request verifiably put live — it drives the "using your back camera, as
   * your verifier asked" line. Cleared the moment the customer flips by hand: that line claims the
   * live lens serves the verifier's request, and after a manual flip it no longer does.
   */
  const [announcedFacing, setAnnouncedFacing] = useState<'ENVIRONMENT' | 'USER' | null>(null);
  const priorFacingRef = useRef<'user' | 'environment' | null>(null);
  // SPEC-13 D4 (item 18): the lens this overlay believes is publishing. The
  // call opens face-forward; the officer auto-apply and the customer flip below
  // both keep it current so the flip toggle targets the right lens.
  const [facing, setFacing] = useState<'user' | 'environment'>('user');
  const [flipping, setFlipping] = useState(false);

  const applyRequestedFacing = useCallback(async () => {
    if (!requestedFacing || !onSwitchCamera) return;
    const target = requestedFacing === 'ENVIRONMENT' ? 'environment' : 'user';
    // The call runs face-forward, so that is what we restore to on completion (CAM-4).
    priorFacingRef.current = 'user';
    const { result, observed, singleCamera } = await onSwitchCamera(target);
    // The attempt is over in ALL THREE cases — the capture must never be held behind a lens the
    // device would not give us.
    setFacingApplied(true);
    setFacingSingleCamera(result === 'unavailable' && singleCamera === true);
    if (result === 'switched') {
      setFacing(observed ?? target);
      setAnnouncedFacing(requestedFacing);
      setFacingUnavailable(false);
      setFacingUnconfirmed(false);
    } else if (result === 'unverified') {
      // Believe the target for the PREVIEW (mirroring has to pick something, and the request is the
      // best information available), but say plainly that it is unconfirmed.
      setFacing(target);
      setAnnouncedFacing(null);
      setFacingUnavailable(false);
      setFacingUnconfirmed(true);
    } else {
      // CAM-5: degrade honestly and let the capture proceed on whatever lens exists. A device with one
      // camera is not an error state, and blocking here would strand the customer.
      setFacing(observed ?? facing);
      setAnnouncedFacing(null);
      setFacingUnavailable(true);
      setFacingUnconfirmed(false);
    }
  }, [requestedFacing, onSwitchCamera, facing]);

  // ── SPEC-13 D4 (item 18): the CUSTOMER-initiated flip ─────────────────────
  // Same switchPublishedFacing ladder as the officer auto-apply (the room owner switches
  // the PUBLISHED track — this overlay never calls getUserMedia), same honest
  // degrade on a one-camera device, and the same CAM-4 restore on close. This
  // is the customer's own lever — and it RETIRES the officer-request line:
  // after a manual flip the live lens no longer serves that request.
  const flipCamera = useCallback(async () => {
    if (!onSwitchCamera || flipping) return;
    setFlipping(true);
    try {
      const target = facing === 'user' ? 'environment' : 'user';
      priorFacingRef.current ??= 'user'; // the call goes back face-forward on close
      const { result, observed, singleCamera } = await onSwitchCamera(target);
      // The customer took the lens back (or tried to): the "as your verifier asked" line is no
      // longer a true statement about the live lens, whatever the outcome was.
      setAnnouncedFacing(null);
      setFacingSingleCamera(result === 'unavailable' && singleCamera === true);
      if (result === 'switched') {
        setFacing(observed ?? target);
        setFacingUnavailable(false);
        setFacingUnconfirmed(false);
      } else if (result === 'unverified') {
        setFacing(target);
        setFacingUnavailable(false);
        setFacingUnconfirmed(true);
      } else {
        setFacing(observed ?? facing);
        setFacingUnavailable(true);
        setFacingUnconfirmed(false);
      }
    } finally {
      setFlipping(false);
    }
  }, [onSwitchCamera, flipping, facing]);

  // CAM-4: restore the prior facing when the overlay closes, so the call — and the recording — go back
  // to showing the customer's face rather than whatever they last photographed.
  useEffect(() => {
    return () => {
      const prior = priorFacingRef.current;
      if (prior && onSwitchCamera) void onSwitchCamera(prior);
    };
  }, [onSwitchCamera]);

  const disposed = useRef(false);
  const videoElRef = useRef<HTMLVideoElement | null>(null);
  const urlsRef = useRef<string[]>([]);
  const ackedRef = useRef<string | null>(null);
  const wrapperStreamRef = useRef<MediaStream | null>(null);

  useEffect(() => {
    disposed.current = false;
    return () => {
      disposed.current = true;
      for (const u of urlsRef.current) URL.revokeObjectURL(u);
      urlsRef.current = [];
    };
  }, []);

  // Ack-on-show — once per prompt: the server records the customer has SEEN who
  // asked and what for. Best-effort: an ack failure never blocks the ceremony.
  useEffect(() => {
    if (ackedRef.current === prompt.id) return;
    ackedRef.current = prompt.id;
    // C10: the ack carries WHAT this customer was shown — the disclosure version, a digest over the
    // load-bearing lines AS RENDERED IN THIS LOCALE, and the locale itself. Digested here, at the
    // point of render, because this is the only place that knows the actual strings painted on this
    // device; the server stores the fact and does not second-guess it.
    const rendered = GCAP_DISCLOSURE_KEYS.map((key) => t(key));
    void disclosureSha256(rendered)
      .catch(() => null)
      .then((sha256) =>
        ackPrompt(sessionId, prompt.id, {
          disclosureVersion: GCAP_DISCLOSURE_VERSION,
          disclosureSha256: sha256,
          disclosureLocale: locale,
        }),
      )
      .catch(() => {
        /* non-blocking — the be lazily reconciles; the capture itself completes the prompt */
      });
  }, [sessionId, prompt.id, t, locale]);

  // Safe-point signal: the poll pauses while a shot is in preview or uploading.
  useEffect(() => {
    onBusyChange(Boolean(shot) || uploading || done);
    return () => onBusyChange(false);
  }, [shot, uploading, done, onBusyChange]);


  // ── the camera handoff: attach the CALL'S OWN published track (never gUM) ──
  const attachVideo = useCallback(
    (el: HTMLVideoElement | null) => {
      const prev = videoElRef.current;
      if (prev && localVideoTrack) localVideoTrack.detach(prev);
      videoElRef.current = el;
      if (el && localVideoTrack) localVideoTrack.attach(el);
    },
    [localVideoTrack],
  );
  useEffect(() => {
    // Track swap (device switch / republish) while the overlay is open: re-attach.
    const el = videoElRef.current;
    if (!el || !localVideoTrack) return undefined;
    localVideoTrack.attach(el);
    return () => {
      localVideoTrack.detach(el);
    };
  }, [localVideoTrack]);

  const trackLive =
    localVideoTrack !== null &&
    camEnabled &&
    localVideoTrack.mediaStreamTrack?.readyState === 'live';

  /**
   * OWNER ROUND 2026-08-29 (I2): apply the officer's lens request THE MOMENT it can be applied —
   * overlay open, published track live — never behind a tap. Guarded per (prompt id, lens): a
   * request that changes mid-flow (a new prompt, or the same prompt re-served asking for the other
   * lens) re-applies; re-renders and state churn do not. `trackLive` is a real precondition, not a
   * nicety: restarting a muted or not-yet-published track would spend the viewfinder blink while
   * there is nothing to restart — so we wait, and the effect re-fires when the track arrives.
   *
   * The callback rides a ref so its identity (it closes over `facing`) never re-triggers the
   * effect, and the cleanup forfeits the guard for an attempt that never COMPLETED: StrictMode's
   * simulated dev unmount fires the CAM-4 restore mid-attempt, and a kept guard would leave the
   * second mount believing the lens was applied when the restore just took it back. A completed
   * attempt keeps its guard — dep churn never re-burns a restart.
   */
  const applyRequestedFacingRef = useRef(applyRequestedFacing);
  applyRequestedFacingRef.current = applyRequestedFacing;
  const facingAppliedForRef = useRef<string | null>(null);
  const facingAttemptDoneRef = useRef(false);
  useEffect(() => {
    if (!requestedFacing || !onSwitchCamera || !trackLive || done) return undefined;
    const tag = `${prompt.id}:${requestedFacing}`;
    if (facingAppliedForRef.current === tag) return undefined;
    facingAppliedForRef.current = tag;
    facingAttemptDoneRef.current = false;
    void applyRequestedFacingRef.current().finally(() => {
      facingAttemptDoneRef.current = true;
    });
    return () => {
      if (!facingAttemptDoneRef.current) facingAppliedForRef.current = null;
    };
  }, [requestedFacing, onSwitchCamera, trackLive, done, prompt.id]);

  // Injection fingerprint bound to THIS guided capture — same track the call
  // publishes, observed read-only (the collector never stops caller tracks).
  const getStream = useCallback((): MediaStream | null => {
    const mst = localVideoTrack?.mediaStreamTrack;
    if (!mst || mst.readyState !== 'live') return null;
    const current = wrapperStreamRef.current;
    if (!current || current.getVideoTracks()[0] !== mst) {
      wrapperStreamRef.current = new MediaStream([mst]);
    }
    return wrapperStreamRef.current;
  }, [localVideoTrack]);
  useCaptureIntegrity(sessionId, getStream, trackLive && !shot && !done);

  /**
   * Owner item 22 — **THE BLACK VIEWFINDER, AND BEING TRAPPED BEHIND IT.**
   *
   * <p>The owner: *"Randomly customer faces is started not coming, this not let us go out of this
   * screen, you stuck for ever."* Two defects in one report, and the second is the worse one.
   *
   * <p>The track can report `live` while the ELEMENT never paints — a racing re-attach, a
   * republished track, autoplay refused after a backgrounded tab. `trackLive` is true, so the
   * overlay showed a guide oval over a black rectangle and a Capture button that would grab nothing.
   * This watches the element itself: no frame dimensions after a grace period means no preview,
   * whatever the track claims.
   */
  const [previewDead, setPreviewDead] = useState(false);
  useEffect(() => {
    if (!trackLive || shot || done) {
      setPreviewDead(false);
      return undefined;
    }
    setPreviewDead(false);
    // Generous: a healthy element paints well inside this, and crying "no preview" over a slow
    // first frame would be its own false alarm.
    const timer = window.setTimeout(() => {
      const el = videoElRef.current;
      const painting = !!el && el.readyState >= 2 && el.videoWidth > 0 && el.videoHeight > 0;
      if (!painting) setPreviewDead(true);
    }, 2500);
    return () => window.clearTimeout(timer);
  }, [trackLive, shot, done, localVideoTrack]);

  /**
   * Owner item 9 — **THE UPLOAD FALLBACK, FINALLY REACHABLE.**
   *
   * <p>Item 9 asked for *"document upload as a fallback when the camera fails"*. The admission
   * config was set (per-tenant, `tenant_doc_upload_admission`) and `DocumentUploadStep` exists —
   * but nothing in the flow mounts it. PH-1 moved document capture IN-CALL and removed DOC_CAPTURE
   * from the customer's pre-call steps; the upload component was left "in the tree, unreferenced".
   * So the configured fallback could not be reached by any customer, which the owner asked about
   * directly. Marking item 9 done on the config alone was wrong.
   *
   * <p>Here is where it belongs: the guided capture is exactly the moment the camera fails. If the
   * preview is dead or the attempt cap is spent, and the tenant admits upload for this document,
   * the customer can send the file instead of being stranded.
   *
   * <p><strong>The honesty ladder is untouched.</strong> An upload is stamped `UPLOAD` /
   * `E4_UPLOADED` by the SERVER (this client sends neither) — a strictly lower evidence grade than a
   * live capture, and the officer still confirms it on the call. Nothing here says "verified".
   */
  const [uploadable, setUploadable] = useState<boolean | null>(null);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [uploadNote, setUploadNote] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const docType = display.docType;

  useEffect(() => {
    // Only document prompts can be uploaded — a selfie or a liveness frame is a live capture by
    // definition, and offering to upload one would be offering to defeat it.
    if (prompt.primitiveKey !== 'doc-show-hold' || !docType) {
      setUploadable(false);
      return undefined;
    }
    let live = true;
    listDocumentRequirements(sessionId)
      .then((reqs) => {
        if (!live) return;
        // Server truth, per tenant and per document. Absent requirement ⇒ NOT offered: a fallback
        // this tenant has not admitted must never appear, and guessing would route a regulated
        // capture down a lower-grade path nobody authorised.
        const match = (reqs ?? []).find(
          (r) => r.docType?.toUpperCase() === docType.toUpperCase(),
        );
        setUploadable(Boolean(match?.allowUpload));
      })
      .catch(() => {
        if (live) setUploadable(false); // unreadable ⇒ not offered (fail-closed)
      });
    return () => {
      live = false;
    };
  }, [sessionId, prompt.primitiveKey, docType]);

  /**
   * Customer-facing copy for an upload refusal — never the server's sentence.
   *
   * The be answers with problem+json aimed at an operator ("prompted upload refused for session
   * 01a03732-… (status=LIVE)"), and until today the droom rendered whatever it got straight onto the
   * customer's phone. A member of the public cannot act on a session UUID or a state machine. The
   * branches below are the refusals a CUSTOMER can actually do something about; everything else says
   * so plainly and points at the person who can help — which is the truthful answer, because on those
   * paths the verifier genuinely is the next step.
   */
  const uploadRefusalCopy = useCallback(
    (e: unknown): string => {
      if (!(e instanceof ApiError)) return t('droom.gcap.upload.failed');
      if (e.status === 403) return t('droom.gcap.upload.notAllowed');
      if (e.status === 409) return t('droom.gcap.upload.already');
      if (e.status === 413) return t('droom.gcap.upload.tooBig');
      if (e.status === 415) return t('droom.gcap.upload.wrongType');
      if (e.status === 422 && /content type|empty|exceeds/i.test(e.message)) {
        return t('droom.gcap.upload.wrongType');
      }
      return t('droom.gcap.upload.failed');
    },
    [t],
  );

  const onUploadFile = useCallback(
    async (file: File): Promise<void> => {
      if (!docType) return;
      setUploadingDoc(true);
      setUploadNote(null);
      try {
        // OWNER DEFECT 2026-08-25 — this used to post to the PRE-WARM document route, whose window
        // closes at IN_QUEUE. On a live call every upload came back 422 "capture window closed", so
        // the fallback appeared at exactly the moment it could not work. It now answers the PROMPT
        // (`:upload`), which is admitted on the live leg, is bound to the officer's request, and is
        // stamped UPLOAD/E4 by the server — the ceremony was live, the evidence was not.
        await uploadPromptDocument(sessionId, prompt.id, file, file.name, discardedReasons.current);
        // The server COMPLETES the prompt on this path, so the officer's worklist advances and the
        // customer is not left holding an open request. (Previously reported as 'withdrawn' because
        // nothing server-side had consumed the prompt — that is no longer true.)
        onDone('completed');
      } catch (e) {
        setUploadNote(uploadRefusalCopy(e));
      } finally {
        setUploadingDoc(false);
      }
    },
    [sessionId, docType, prompt.id, onDone, uploadRefusalCopy],
  );

  /** Re-attach the published track and ask the element to play again. */
  const retryPreview = useCallback(() => {
    const el = videoElRef.current;
    setPreviewDead(false);
    if (el && localVideoTrack) {
      localVideoTrack.detach(el);
      localVideoTrack.attach(el);
      void el.play?.().catch(() => {
        /* autoplay refused — the honest note stays up and the customer can still speak */
      });
    }
  }, [localVideoTrack]);

  // Pre-shot exposure cue (advisory; null → neutral frame, no claim).
  const getVideo = useCallback(() => videoElRef.current, []);
  const exposure = useViewfinderExposure(getVideo, trackLive && !shot && !done);

  // ── capture from the call track's viewfinder (raw frame, native size) ──────
  const captureFrame = useCallback(async (): Promise<Blob | null> => {
    const grab = async (): Promise<Blob | null> => {
      const el = videoElRef.current;
      if (!el || el.readyState < 2) return null;
      const w = el.videoWidth;
      const h = el.videoHeight;
      if (!w || !h) return null;
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) return null;
      ctx.drawImage(el, 0, 0, w, h); // the RAW sensor frame — never the CSS-mirrored preview
      return new Promise<Blob | null>((resolve) => {
        canvas.toBlob((b) => resolve(b), 'image/jpeg', JPEG_QUALITY);
      });
    };

    /*
     * OWNER DEFECT 2026-08-25 — "from my mobile when trying to capture, always getting 'this
     * camera's photo is too small'; on laptop this is working fine."
     *
     * The call publishes the camera with no capture constraints, so the browser negotiates whatever
     * it likes — routinely 640x480 on a phone, against a 600px document floor. We were grabbing that
     * video-call frame and then telling the customer their DEVICE was inadequate. The phone in their
     * hand has the better camera of the two; the constraint was ours.
     *
     * A document capture now asks the live track for a still-grade frame first, and puts the call's
     * own resolution back afterwards. Only for DOCUMENTS: a selfie/liveness frame is judged on a
     * lower floor and does not need the bandwidth, and raising it would slow the very moment the
     * officer is watching for.
     */
    const isDocument = prompt.primitiveKey === 'doc-show-hold';
    if (!isDocument) return grab();
    return withStillQuality(localVideoTrack?.mediaStreamTrack ?? null, grab);
  }, [prompt.primitiveKey, localVideoTrack]);

  /**
   * The frame LANDED: show the done card, then return to the call. The ONE way a sent frame is
   * acknowledged — the ordinary 201 and the re-queried transport failure below both end here, so
   * the two paths cannot drift apart.
   */
  const markSent = useCallback((): void => {
    setShot(null);
    setDone(true);
    window.setTimeout(() => {
      if (!disposed.current) onDone('completed');
    }, DONE_AUTORETURN_MS);
  }, [onDone]);

  /**
   * LIVE DEFECT 2026-08-26 — **"try again" for an upload that had already succeeded.**
   *
   * <p>A two-party probe on the preview lost the network 19 s into `:capture`
   * (`net::ERR_NETWORK_CHANGED`). The server had COMMITTED the frame 18 s earlier — its answer was
   * merely late behind a slow third-party OCR call (nginx logged 499 once the client gave up). The
   * customer was told "That didn't upload — please try again"; a retake appended a duplicate
   * attempt; and the officer watched a prompt the server had already closed stay open on the
   * customer's side.
   *
   * <p>A transport failure is NOT a verdict on the upload: the request may have landed and only the
   * response died on the way back. So before saying "try again", ask the server ONCE whether THIS
   * prompt is still pending. Not pending (null, or a different prompt now) ⇒ the frame is in and the
   * prompt went terminal ⇒ finish exactly as a 201 would. Still pending ⇒ the honest "try again".
   *
   * <p>Bounded and fail-safe: one re-query, and if that itself fails we are back to not knowing —
   * which is today's copy, never a fabricated success. Deliberately NO client-side timeout on the
   * upload: the server may legitimately take ~10 s (it is being made to answer faster separately).
   */
  const captureLanded = useCallback(async (): Promise<boolean> => {
    try {
      const pending = await fetchPendingPrompt(sessionId);
      return pending === null || pending.id !== prompt.id;
    } catch {
      return false;
    }
  }, [sessionId, prompt.id]);

  /**
   * Upload one accepted shot, NAMING who pressed the shutter for it (V116 / item 4).
   *
   * <p>The actor is passed at send time rather than held in state: a frame the verifier triggered and
   * a frame the customer retook are two different provenances, and reading the answer off a mutable
   * flag is exactly how the two get confused.
   */
  const sendShot = useCallback(
    async (accepted: Shot, actor: ShutterActor): Promise<void> => {
      setUploading(true);
      setNote(null);
      try {
        await uploadPromptCapture(
          sessionId, prompt.id, accepted.blob, discardedReasons.current, actor,
        );
        if (disposed.current) return;
        markSent();
      } catch (e) {
        if (disposed.current) return;
        if (e instanceof ApiError && (e.status === 409 || e.status === 404)) {
          // The prompt went terminal meanwhile (withdrawn / expired / already
          // captured) — the outcome is recorded server-side; return calmly.
          onDone('withdrawn');
          return;
        }
        if (e instanceof ApiError && e.status === 422) {
          setNote(e.message); // the server names the problem honestly
          return;
        }
        if (!(e instanceof ApiError)) {
          // No answer at all (network drop / abort) — the server may still hold the frame. The
          // parent's prompt poll cannot race this: `shot` and `uploading` keep it paused until we
          // have decided, and a paused poll never calls onDone.
          const landed = await captureLanded();
          if (disposed.current) return;
          if (landed) {
            markSent();
            return;
          }
        }
        setNote('droom.gcap.uploadFail');
      } finally {
        if (!disposed.current) setUploading(false);
      }
    },
    [sessionId, prompt.id, onDone, markSent, captureLanded],
  );

  /**
   * Take one frame. `actor` names who pressed.
   *
   * <p>An OFFICER-triggered shot that passes the on-device quality gate is SENT without a further
   * customer press. That is the point of owner item 4: the customer acknowledged the purpose as its
   * own act, then held the document up, and the verifier chose the moment — the same ceremony as a
   * counter-side KYC. A shot that FAILS quality is never auto-sent: it lands in the ordinary retake
   * state, where the customer sees what was wrong and the officer can fire again.
   */
  const takeShot = useCallback(
    async (actor: ShutterActor): Promise<void> => {
      if (assessing || uploading || done) return;
      setAssessing(true);
      setNote(null);
      try {
        const blob = await captureFrame();
        if (disposed.current) return;
        if (!blob) {
          setNote('droom.gcap.notReady');
          return;
        }
        const verdict = await assessCaptureQuality(blob, config.kind);
        if (disposed.current) return;
        const url = URL.createObjectURL(blob);
        urlsRef.current.push(url);
        setAttempts((n) => n + 1);   // CAP-4: every attempt counts, including ones the customer discards
        const taken: Shot = { blob, url, verdict };
        setShot(taken);
        if (actor === 'OFFICER' && verdict.ok) {
          await sendShot(taken, 'OFFICER');
        }
      } finally {
        if (!disposed.current) setAssessing(false);
      }
    },
    [assessing, uploading, done, captureFrame, config.kind, sendShot],
  );

  const onCapture = useCallback((): Promise<void> => takeShot('CUSTOMER'), [takeShot]);

  // ── V116 / owner item 4: READINESS, and the officer's remote shutter ───────
  //
  // Readiness is reported ONCE, and only when BOTH halves are true: the customer has answered the
  // purpose notice themselves, and the published camera track is actually live. The server refuses
  // the officer's shutter until this lands, so this report is the hinge the whole DPDP posture hangs
  // on — it must never be sent on the strength of the overlay merely being on screen.
  const readyReportedRef = useRef(false);
  useEffect(() => {
    if (readyReportedRef.current) return;
    if (!purposeAcknowledged || !trackLive) return;
    readyReportedRef.current = true;
    void reportReady(sessionId, prompt.id).catch(() => {
      // Non-blocking: the customer's own shutter still works exactly as before. The officer simply
      // does not get the remote one, which is the correct degrade — never the other way round.
    });
  }, [purposeAcknowledged, trackLive, sessionId, prompt.id]);

  // The officer fired. Every precondition is re-checked HERE, on the device that owns the camera:
  // a server that has been convinced the surface is ready still cannot make this client shoot while
  // the customer has a shot in preview, the camera off, or the attempt cap spent.
  const [remoteFiring, setRemoteFiring] = useState(false);
  const actedShutterRef = useRef(0);
  useEffect(() => {
    const tick = remoteShutterAt ?? 0;
    if (tick === 0 || tick === actedShutterRef.current) return;
    actedShutterRef.current = tick;
    if (!purposeAcknowledged || !trackLive || shot || done || uploading || attemptsExhausted) {
      return; // a shutter that cannot be honoured is dropped, never queued to surprise them later
    }
    setRemoteFiring(true);
    void takeShot('OFFICER').finally(() => {
      if (!disposed.current) setRemoteFiring(false);
    });
  }, [
    remoteShutterAt, purposeAcknowledged, trackLive, shot, done, uploading,
    attemptsExhausted, takeShot,
  ]);

  const onRetake = (): void => {
    if (shot) {
      // CAP-4: the reason is captured HERE, at the moment of discard, because it is the only moment it
      // is known — the verdict dies with the object URL. A shot the quality gate passed and the
      // customer retook anyway is recorded as CUSTOMER_RETAKE: "they chose to" is itself a finding,
      // and silently dropping it would make the ladder read as if the gate rejected everything.
      discardedReasons.current.push(shot.verdict.reason ?? 'CUSTOMER_RETAKE');
      URL.revokeObjectURL(shot.url);
      urlsRef.current = urlsRef.current.filter((u) => u !== shot.url);
    }
    setShot(null);
    setNote(null);
  };

  const onAccept = async (): Promise<void> => {
    if (!shot || !shot.verdict.ok || uploading) return;
    await sendShot(shot, 'CUSTOMER');
  };

  // Viewfinder hint — same reason as the guide colour, in words (WCAG 1.4.1).
  const hint = ((): string => {
    if (done) return '';
    // V116 / item 4: the verifier pressed. NEVER silent — a photograph taken of someone without a
    // visible cue is a covert capture, whatever the consent record says. This is the cue.
    if (remoteFiring) return t('droom.gcap.remote.firing');
    // Item 22: say the preview is not showing rather than presenting a guide oval over a black
    // rectangle and a shutter that would capture nothing.
    if (previewDead) return t('droom.gcap.preview.dead');
    if (shot) {
      if (!shot.verdict.ok) return t(shot.verdict.messageKey ?? 'droom.gcap.retryLine');
      if (uploading) return t('droom.gcap.sendingLong');
      return t('droom.gcap.looksSharp');
    }
    if (assessing) return t('droom.gcap.checkingQuality');
    if (!trackLive) return t('droom.gcap.camOff');
    if (exposure === 'too-dark') return t('droom.selfie.hint.toodark');
    if (exposure === 'too-bright') return t('droom.selfie.hint.toobright');
    // SPEC-13 D4: the registry's per-type guidance wins over the generic line.
    // #16: with no served guidance, a side-named prompt still says which side.
    if (display.captureGuidance) return display.captureGuidance;
    if (display.docSide === 'BACK') return t('droom.gcap.side.back');
    if (display.docSide === 'FRONT') return t('droom.gcap.side.front');
    return t(config.instructionKey);
  })();

  // Item 19: ADVISORY only — a landscape-aspect document photographed in
  // portrait gets a "rotate your phone" hint over the guide; nothing blocks.
  const portrait = useIsPortrait();
  const rotateAdvisory =
    display.guideAspect === 'LANDSCAPE' && portrait && trackLive && !shot && !done;

  const guideState = exposure ? 'warn' : 'neutral';
  // Mirror ONLY the face-forward preview (a mirrored rear-camera document is
  // unreadable); the uploaded frame is always the RAW sensor frame regardless.
  const mirrored: CSSProperties =
    facing === 'user'
      ? { objectFit: 'cover', transform: 'scaleX(-1)' }
      : { objectFit: 'cover' };

  return (
    <div className="gcap" role="dialog" aria-modal="true" aria-label={title}>
      {/* Who asked + what for — shown BEFORE anything is captured (REG floor). */}
      <div className="gcap-head">
        <span className="gcap-badge">
          <Icon name="camera" size={14} />
          {t('droom.gcap.badge')}
        </span>
        <div className="gcap-title">{title}</div>
        <p className="gcap-why">
          {t('droom.gcap.asked')} {t(categoryKey(String(prompt.reasonCategory)))}
        </p>
      </div>

      {/* CAP-1/CAP-2: the purpose notice and its OWN affirmative act, before any capture control
          exists. Six items because DPDP s.5 requires the customer to know what is collected, what it
          is compared against, who processes it, how long it is kept, that it is not used to train
          models, and how to withdraw — a "your verifier asked for a photo" line is disclosure of the
          REQUEST, not of the PURPOSE. */}
      {!purposeAcknowledged && !done ? (
        <div className="gcap-purpose" role="group" aria-label={t('droom.gcap.purpose.title')}>
          <div className="gcap-purpose-title">{t('droom.gcap.purpose.title')}</div>
          <ul className="gcap-purpose-list">
            <li>{t('droom.gcap.purpose.what')}</li>
            <li>{t('droom.gcap.purpose.comparedWith')}</li>
            <li>{t('droom.gcap.purpose.processor')}</li>
            <li>{t('droom.gcap.purpose.retention')}</li>
            <li>{t('droom.gcap.purpose.noTraining')}</li>
            <li>{t('droom.gcap.purpose.withdraw')}</li>
          </ul>
          <Button onClick={() => setPurposeAcknowledged(true)}>
            {t('droom.gcap.purpose.agree')}
          </Button>
        </div>
      ) : null}

      {/* OWNER ROUND 2026-08-29 (I2): the officer's lens request is APPLIED on open and narrated —
          never parked behind a confirm tap (see the CAM-1..CAM-5 note above). While the switch is
          in flight this line says what was asked; the lines below then say what actually happened.
          Rendered only when a preference was actually expressed AND this room can act on one: a
          null requestedFacing means the officer said nothing, and mock mode has no switch to run —
          narrating a choice nobody made, or an act that cannot happen, would be noise. */}
      {requestedFacing && onSwitchCamera && !facingApplied && !done ? (
        <div className="gcap-facing" role="status">
          <p className="gcap-facing-copy">
            {requestedFacing === 'ENVIRONMENT'
              ? t('droom.gcap.facing.rear')
              : t('droom.gcap.facing.front')}
          </p>
        </div>
      ) : null}
      {announcedFacing && !done ? (
        /* The switch the browser CONFIRMED, named plainly — with the promise CAM-4 keeps. */
        <p className="gcap-facing-note" role="status">
          {announcedFacing === 'ENVIRONMENT'
            ? t('droom.gcap.facing.applied.rear')
            : t('droom.gcap.facing.applied.front')}
        </p>
      ) : null}
      {facingSingleCamera ? (
        /* The device provably has ONE camera: "we couldn't switch" would overstate it. One camera,
           we are using it, and the capture proceeds (CAM-5). */
        <p className="gcap-facing-note" role="status">
          {t('droom.gcap.facing.oneCamera')}
        </p>
      ) : facingUnavailable ? (
        <p className="gcap-facing-note" role="status">
          {t('droom.gcap.facing.unavailable')}
        </p>
      ) : null}
      {/* The restart worked; the browser would not say which lens came back. We claim nothing and
          point at the viewfinder — never a silent "REAR CAMERA" we cannot stand behind. */}
      {facingUnconfirmed && !facingUnavailable ? (
        <p className="gcap-facing-note" role="status">
          {t('droom.gcap.facing.unconfirmed')}
        </p>
      ) : null}

      <div className="gcap-viewport">
        {done ? (
          <div className="gcap-done" role="status">
            <span className="gcap-done-badge">
              <Icon name="check" size={26} />
            </span>
            <div className="gcap-done-title">{t('droom.gcap.done.title')}</div>
            <div className="gcap-done-sub">{t('droom.gcap.done.sub')}</div>
          </div>
        ) : shot ? (
          <img
            src={shot.url}
            alt={t('droom.capture.previewAlt')}
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <video ref={attachVideo} autoPlay playsInline muted style={mirrored} className="gcap-video" />
        )}

        {!shot && !done && trackLive ? (
          <GuideFrame state={guideState} face={config.faceGuide} card={config.cardGuide} />
        ) : null}

        {rotateAdvisory ? (
          /* Item 19: advisory over the guide frame — never a gate. */
          <div
            className="vp-hint"
            role="status"
            style={{ top: 12, bottom: 'auto' }}
          >
            {t('droom.gcap.rotate')}
          </div>
        ) : null}

        {hint ? (
          <div className="vp-hint" role={shot && !shot.verdict.ok ? 'alert' : 'status'}>
            {hint}
          </div>
        ) : null}
      </div>

      {note ? (
        <div className="consent-note mono" role="alert">
          {t(note)}
        </div>
      ) : null}

      <div className="gcap-actions">
        {done ? (
          <Button onClick={() => onDone('completed')}>{t('droom.gcap.back')}</Button>
        ) : shot ? (
          !shot.verdict.ok ? (
            <Button onClick={onRetake}>{t('droom.action.retake')}</Button>
          ) : uploading ? (
            <Button disabled>{t('droom.gcap.sending')}</Button>
          ) : (
            <>
              <Button onClick={() => void onAccept()}>{t('droom.gcap.send')}</Button>
              <Button variant="ghost" onClick={onRetake}>
                {t('droom.action.retake')}
              </Button>
            </>
          )
        ) : !purposeAcknowledged ? null : attemptsExhausted ? (
          // CAP-4: the cap is reached, so the customer is NOT left pressing a dead button. The copy
          // names a QUALITY problem and asks them to speak to the officer — it never says a MATCH
          // failed, because telling the subject their face did not match is free coaching for anyone
          // trying to defeat it.
          <span className="gcap-exhausted" role="status">
            {t('droom.gcap.attempts.exhausted')}
          </span>
        ) : trackLive ? (
          <>
            <Button onClick={() => void onCapture()} disabled={assessing || remoteFiring}>
              {remoteFiring
                ? t('droom.gcap.remote.firing')
                : assessing
                  ? t('droom.capture.assessing.short')
                  : t('droom.capture.shoot')}
            </Button>
            {/* Item 18: the customer's own flip — same published-track switch
                as the officer ceremony, honest false on a one-camera device. */}
            {onSwitchCamera ? (
              <Button
                variant="ghost"
                disabled={flipping}
                onClick={() => void flipCamera()}
              >
                {flipping ? t('droom.action.pleaseWait') : t('droom.gcap.flip')}
              </Button>
            ) : null}
          </>
        ) : (
          <Button onClick={onToggleCam}>{t('droom.call.camOn')}</Button>
        )}
        {/* Item 22 — RECOVERY, and a way back to the call.
            The owner's report was that a black viewfinder left the customer "stuck for ever": this
            is a modal with no dismiss, by design (declining is spoken to the verifier so the outcome
            is RECORDED, never a silent dismissal). That design is right and the trap was not — with
            a dead preview the customer could neither capture nor speak to anyone about it.

            "Back to the call" does NOT decline and does NOT cancel: the prompt stays pending
            server-side, the officer still sees it, and the poll re-opens this overlay when the
            customer is ready. It simply returns them to the call so they can TALK. No outcome is
            invented, and nobody is held behind a black screen. */}
        {!done && previewDead ? (
          <div className="gcap-recover" role="group" aria-label={t('droom.gcap.preview.dead')}>
            <Button variant="ghost" onClick={retryPreview}>
              {t('droom.gcap.preview.retry')}
            </Button>
            <Button variant="ghost" onClick={() => onDone('withdrawn')}>
              {t('droom.gcap.backToCall')}
            </Button>
          </div>
        ) : null}

        {/* Item 9 — the upload fallback, offered where the camera has actually FAILED (a dead
            preview or a spent attempt cap) and only for a document this tenant admits uploads for.
            Never a shortcut past a working camera: a live capture is the higher evidence grade and
            stays the default path. The server stamps UPLOAD / E4_UPLOADED itself. */}
        {!done && uploadable && (previewDead || attemptsExhausted) ? (
          <div className="gcap-upload" role="group" aria-label={t('droom.gcap.upload.title')}>
            <input
              ref={fileRef}
              type="file"
              accept="image/jpeg,image/png,application/pdf"
              hidden
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void onUploadFile(f);
                e.target.value = '';
              }}
            />
            <Button
              variant="ghost"
              disabled={uploadingDoc}
              onClick={() => fileRef.current?.click()}
            >
              {uploadingDoc ? t('droom.gcap.upload.sending') : t('droom.gcap.upload.cta')}
            </Button>
            <p className="gcap-upload-note">{t('droom.gcap.upload.grade')}</p>
            {uploadNote ? (
              <p className="consent-note mono" role="alert">
                {uploadNote}
              </p>
            ) : null}
          </div>
        ) : null}
        {!done ? (
          <>
            {/* Item 4: the customer is TOLD, before it can happen, that their verifier may take the
                photograph — so a remote shutter is never a surprise. Shown only once they have
                acknowledged the purpose, because that is when it becomes true. */}
            {purposeAcknowledged ? (
              <div className="gcap-decline" role="status">
                {t('droom.gcap.remote.mayCapture')}
              </div>
            ) : null}
            <div className="gcap-decline">{t('droom.gcap.decline')}</div>
          </>
        ) : null}
      </div>
    </div>
  );
}
